import { IElection } from '@/shared/api-client/models/election/election';
import { ArgumentValidator, ComplexIdHelper, ConsoleFacade, NameOf } from '@/shared/helpers';
import { IElection as IOldElection } from '@/shared/models/election';
import { EventBusService } from '@/shared/services/event-bus-service';
import { Component, Emit, Inject, Prop, Vue } from 'vue-property-decorator';
import VoteComponent from '@/public/vote/components/vote-component/vote-component.vue';
import { IActivityEvent } from '../../models/activity-event';
import { IActivity } from '@/shared/api-client/models/activity';
import { IActivitySettings } from '@/shared/models/activity-settings';
import { IElectionResult } from '@/shared/api-client/models/election-result';
import { ISumResultScore } from '@/shared/api-client/models/sum-result-score';
import { IIrvResultScore } from '@/shared/api-client/models/irv-result-score';
import SumElectionResultListComponent from '../election-result-list/sum-result-list-component/sum-result-list-component.vue';
import IrvElectionResultListComponent from '../election-result-list/irv-result-list-component/irv-result-list-component.vue';
import { IPreparedAdditionalData } from '@/public/vote/models/prepared-additional-data';
import { UIEventHandler } from '@/shared/helpers/ui-event-handler';

type ViewState = 'Loading' | 'Waiting' | 'Vote' | 'Result';

interface IViewModel {

    isLoading: boolean;

    election?: IElection;

    preparedAdditionalDataForElection?: IPreparedAdditionalData[];

    sumResult?: IElectionResult<ISumResultScore>;

    irvResult?: IElectionResult<IIrvResultScore>;

    hasVotedIn: string[];
}

@Component({
    components: {
        VoteComponent,
        SumElectionResultListComponent,
        IrvElectionResultListComponent
    }
})
export default class ActivityStreamComponent extends Vue {

    @Inject('activityEventBus') readonly activityEventBus!: EventBusService<IActivityEvent>;

    @Prop() public activity!: IActivity;
    @Prop() public settings!: IActivitySettings;
    @Prop() public votingTranslations!: { [key: string]: string; };
    @Prop() public resultTranslations!: { [key: string]: string; };
    @Prop({default: []}) public initialVotedIn!: string[];
    @Prop({ default: false}) isSpectator!: boolean;

    @Emit('spectatorClicked')
    // tslint:disable-next-line:no-empty
    public emitSpectatorClicked(): void {}

    public viewModel: IViewModel = {
        isLoading: false,
        hasVotedIn: []
    };

    public get hasMessage(): boolean {

        switch(this.currentState) {

            case 'Vote':

                if (this.viewModel.election && this.hasVotedInCurrentElection) {
                    return true;
                }

                return false;

            case 'Result':
                return false;

            case 'Loading':
            case 'Waiting':
                return true;
        }

        return false;
    }

    public get hasVotedInCurrentElection(): boolean {

        if (!this.viewModel.election) {
            return false;
        }

        return this.hasVotedInElection(this.viewModel.election.id);
    }

    public hasVotedInElection(electionId: string): boolean {

        ArgumentValidator.notNullOrEmpty(electionId, `electionId`);

        const fullElectionId = ComplexIdHelper.createId({
            prefix: 'elections',
            id: electionId
        });

        return this.viewModel.hasVotedIn
            .map(x => x.toLowerCase())
            .includes(fullElectionId.toLowerCase());
    }

    public get currentState(): ViewState {

        if (!this.viewModel) {
            return 'Loading';
        }

        if (this.viewModel.election && (this.viewModel.sumResult || this.viewModel.irvResult)) {
            return 'Result';
        }

        if (this.viewModel.election && this.viewModel.election.state === 'Voting') {
            return 'Vote';
        }

        return 'Waiting';
    }

    public async created() {

        const activityEventFunc = (event?: IActivityEvent) => {

            if (!event) {
                return;
            }

            this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.isLoading), true);

            ConsoleFacade.log(`ActivityStreamComponent: Got activity event`, event);

            if (this.viewModel.election?.id !== event.election?.id || this.viewModel.election?.state !== event.election?.state) {

                ConsoleFacade.log(`ActivityStreamComponent: Changed election`, this.viewModel.election?.id, event.election?.id);

                if (this.viewModel.election?.id) { // Election change after the initial load

                    this.killModalBackdrop();
                    this.scrollToTop();
                }

                const preparedAdditionalDataForElection: IPreparedAdditionalData[] = [];

                if (event.election && event.candidates && event.candidates.length > 0) {

                    preparedAdditionalDataForElection.push({
                        electionId: event.election?.id,
                        candidates: event.candidates
                    });
                }

                this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.election), event.election ?? undefined);
                this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.preparedAdditionalDataForElection), preparedAdditionalDataForElection ?? undefined);
            }

            this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.sumResult), event.sumResult ?? undefined);

            this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.irvResult), event.irvResult ?? undefined);

            this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.isLoading), false);
        };

        activityEventFunc(this.activityEventBus.current);

        this.activityEventBus.$on('changed', activityEventFunc);

        if (this.initialVotedIn.length > 0) {

            this.viewModel.hasVotedIn = this.initialVotedIn;
        }
    }

    public async handleVoteStored(electionId: string): Promise<void> {

        ArgumentValidator.notNullOrEmpty(electionId, `electionId`);

        this.viewModel.hasVotedIn.push(electionId);
    }

    public spectatorOverlayClicked($event?: UIEvent): void {

        UIEventHandler.preventDefaults($event);

        this.emitSpectatorClicked();
    }

    public mapElection(election: IElection): IOldElection {

        ArgumentValidator.notNullOrEmpty(election, `election`);

        const mappedElection: IOldElection = {
            electionId: { prefix: 'elections', id: election.id },
            electionName: election.name,
            electionDescription: election.settings?.description || '',
            ballotBoxEngineType: election.settings?.ballotBoxEngineType || 'Encrypted',
            electionType: 'Candidacy',
            elections: [],

            separateCandidatesNominatedByCommittee: election.nominatedByCommitteeSettings?.separateFromOther,
            autoSelectCandidatesNominatedByCommittee: election.nominatedByCommitteeSettings?.autoSelect,
            numberCandidatesNominatedByCommittee: election.nominatedByCommitteeSettings?.enableNumbering,

            candidatureDisplayType: election.candidateSettings?.displayType,
            showGenderForUserCandidates: election.candidateSettings?.showGender,

            enableBlankVote: election.settings?.allowBlankVotes,
            minNumberOfCandidatures: election.selectionLimitSettings?.min,
            maxNumberOfCandidatures: election.selectionLimitSettings?.max,

            isOpenForNomination: election.state === 'Nomination',
            allowSelfNomination: election.nominationSettings?.allowSelfNomination,

            isLocked: election.settings?.isLocked,

            requiredEqualGenderDivision: election.settings?.requireEqualGenderDivision,
            voteEncryptionPublicKey: election.settings?.publicEncryptionKey,
            votingType: election.settings?.votingType
        }

        return mappedElection;
    }

    private killModalBackdrop(): void {

        $('.modal-backdrop').hide();
    }

    private scrollToTop(): void {

        const containerElement = $('#easyvote-pageContainer').get(0);
        const scrollTargetElement = $('#easyvote-pageContainer__scrollTarget').get(0);

        if (!containerElement || !scrollTargetElement) { // If scroll elements does not exist

            const embeddedWrapper = $('#easyvote-pageWrapper-embedded').get(0);

            if (embeddedWrapper) { // If running in embedded mode

                window.scrollTo(0, 0);
            }

            return;
        }

        containerElement.scrollTop = 0;
    }
}
